"use client";

import React, { useEffect, useRef } from "react";
import Modal from "./Modal";
import { X } from "lucide-react";

interface VideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  videoSource?: string; // Local video file path
  youtubeVideoId?: string; // YouTube video ID
  startSeconds?: number; // Start offset in seconds for YouTube
  title?: string;
  autoPlay?: boolean;
  muted?: boolean;
  controls?: boolean;
}

/**
 * Universal Video Modal Component
 * Supports both local video files and YouTube embeds
 * Consistent UX with close button top-right
 */
const VideoModal: React.FC<VideoModalProps> = ({
  isOpen,
  onClose,
  videoSource,
  youtubeVideoId,
  startSeconds,
  title = "Video ansehen",
  autoPlay = true,
  muted = true,
  controls = true,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Auto-play local video when modal opens
  useEffect(() => {
    if (isOpen && videoSource && videoRef.current) {
      const timer = setTimeout(() => {
        videoRef.current?.play().catch((error) => {
          console.log("Video autoplay failed:", error);
        });
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isOpen, videoSource]);

  // Clean up iframe when modal closes
  useEffect(() => {
    if (!isOpen && iframeRef.current) {
      iframeRef.current.src = "";
    }
  }, [isOpen]);

  const getYouTubeEmbedUrl = (videoId: string) => {
    const autoplayParam = autoPlay ? "1" : "0";
    const muteParam = muted ? "1" : "0";
    const startParam = startSeconds ? `&start=${startSeconds}` : "";
    return `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=${autoplayParam}&mute=${muteParam}&rel=0&modestbranding=1&playsinline=1${startParam}`;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      variant="video"
      size="custom"
      maxWidth="min(90vw, 1200px)"
      maxHeight="auto"
      showCloseButton={false}
      closeOnOverlayClick={true}
      closeOnEscape={true}
      fullscreenOnMobile={true}
      className="video-modal-clean"
    >
      {/* Clean Video Container with proper 16:9 aspect ratio */}
      <div className="relative w-full bg-black">
        {/* Local Video with 16:9 aspect ratio */}
        {videoSource && (
          <div className="relative w-full aspect-video">
            <video
              ref={videoRef}
              controls={controls}
              autoPlay={autoPlay}
              muted={muted}
              className="absolute inset-0 w-full h-full object-cover"
              style={{
                border: "none",
                outline: "none",
                display: "block",
              }}
              preload="metadata"
            >
              <source src={videoSource} type="video/mp4" />
              <source
                src={videoSource.replace(".mp4", ".webm")}
                type="video/webm"
              />
              Your browser does not support the video tag.
            </video>
          </div>
        )}

        {/* YouTube Video with 16:9 aspect ratio */}
        {youtubeVideoId && (
          <div className="relative w-full aspect-video">
            <iframe
              ref={iframeRef}
              src={isOpen ? getYouTubeEmbedUrl(youtubeVideoId) : ""}
              title={title}
              className="absolute inset-0 w-full h-full border-0"
              frameBorder="0"
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              referrerPolicy="strict-origin-when-cross-origin"
              style={{
                border: "none",
                outline: "none",
                display: "block",
              }}
            />
          </div>
        )}

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 z-20 bg-black/70 hover:bg-black/90 text-white rounded-full p-2 transition-all duration-200 shadow-lg backdrop-blur-sm"
          aria-label="Video schließen"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </Modal>
  );
};

export default VideoModal;
